import React from "react";
import { Link } from "react-router-dom";
import { Scale, Shield, FileText, Mail, AlertTriangle, Lock } from "lucide-react";
import SEO from "../components/SEO";

const sections = [
  {
    id: "terms",
    icon: FileText,
    title: "Terms of Use",
    body: (
      <>
        <p className="mb-3">
          By accessing Paws on Record (the "Service") you agree to these Terms
          of Use. The Service is provided for informational, research, and
          public-interest purposes only.
        </p>
        <ul className="list-disc ml-5 space-y-2 text-sm">
          <li>
            You will not use the Service to harass, intimidate, threaten, stalk,
            or commit violence against any individual listed.
          </li>
          <li>
            You will not use the Service for any decision governed by the Fair
            Credit Reporting Act (15 U.S.C. § 1681 et seq.), including
            employment, tenancy, insurance, or consumer credit decisions. The
            Service is not a consumer reporting agency and does not produce
            consumer reports.
          </li>
          <li>
            You will not scrape, republish, or redistribute the Service's
            contents in bulk without written permission. Authoritative source
            records remain the property of their issuing agencies.
          </li>
          <li>
            You will not use the Service to discriminate in housing, lending,
            public accommodation, or employment in violation of any applicable
            law.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "accuracy",
    icon: Scale,
    title: "Accuracy, Not Legal Advice",
    body: (
      <>
        <p className="mb-3">
          Every record on Paws on Record links to an authoritative public source
          (a state or county animal-abuser registry, a DOJ press release, an
          appellate decision, or a published news account). We encourage users
          to verify each entry against that source before relying on it.
        </p>
        <p className="mb-3">
          Information may contain errors, omissions, or records that have been
          superseded by appellate reversal, expungement, pardon, or record
          sealing. The Service is not legal, investigative, or consumer advice.
        </p>
        <p>
          Adoption, fostering, and rescue decisions should always be made using
          multiple sources including a formal background check where
          appropriate.
        </p>
      </>
    ),
  },
  {
    id: "dmca",
    icon: AlertTriangle,
    title: "Corrections, Takedowns & DMCA",
    body: (
      <>
        <p className="mb-3">
          If a record on Paws on Record is inaccurate — for example the
          conviction has been overturned, vacated, expunged, sealed, or the
          listed individual is not the subject of the source record — contact us
          using the form on our Report page or email the address below with:
        </p>
        <ul className="list-disc ml-5 space-y-1 text-sm mb-3">
          <li>The case ID shown on the record</li>
          <li>A short description of the inaccuracy</li>
          <li>A link to the authoritative corrected source</li>
        </ul>
        <p>
          We aim to respond within 5 business days. DMCA notices alleging
          copyright infringement should be sent to the designated agent at the
          same address.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    icon: Lock,
    title: "Privacy",
    body: (
      <>
        <p className="mb-3">
          Paws on Record stores the following data:
        </p>
        <ul className="list-disc ml-5 space-y-1 text-sm mb-3">
          <li>Publicly available conviction information from cited sources.</li>
          <li>
            Any details voluntarily submitted through the Report form, including
            optional video evidence, which we forward to appropriate authorities.
          </li>
          <li>
            Basic request logs (IP, user agent) retained for 90 days for
            security and abuse prevention.
          </li>
        </ul>
        <p className="mb-3">
          We do not sell user data. We do not use third-party advertising
          trackers. Video evidence is stored only for as long as necessary to
          forward to authorities unless we are legally required to retain it.
        </p>
        <p>
          Geocoding lookups are made via OpenStreetMap's Nominatim service. Map
          tiles are served by OpenStreetMap. No user identifier is sent to
          those services.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    icon: Shield,
    title: "Limitation of Liability",
    body: (
      <>
        <p className="mb-3">
          THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. TO THE
          MAXIMUM EXTENT PERMITTED BY LAW, PAWS ON RECORD AND ITS OPERATORS
          DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT.
        </p>
        <p>
          IN NO EVENT SHALL THE OPERATORS BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF USE OR
          INABILITY TO USE THE SERVICE, INCLUDING DAMAGES ARISING FROM RELIANCE
          ON DATA, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
      </>
    ),
  },
  {
    id: "prohibited",
    icon: AlertTriangle,
    title: "Strictly Prohibited",
    body: (
      <>
        <p className="mb-3">
          You may not use Paws on Record to:
        </p>
        <ul className="list-disc ml-5 space-y-1 text-sm">
          <li>Physically confront, threaten, harass, or harm any listed individual or their family</li>
          <li>Publish identifying information (home address, workplace, vehicle plate) beyond what already appears on the public source</li>
          <li>Make decisions protected by the Fair Credit Reporting Act</li>
          <li>Engage in unlawful discrimination, vigilantism, or retaliation</li>
          <li>Create a derivative registry or republish entries without attribution to the authoritative source</li>
        </ul>
      </>
    ),
  },
  {
    id: "contact",
    icon: Mail,
    title: "Contact",
    body: (
      <>
        <p>
          For corrections, takedown requests, DMCA notices, or other legal
          matters, please use the takedown form linked on each individual
          record's detail page, or contact the site operators at{" "}
          <a href="mailto:info@pawsonrecord.com" className="font-mono underline hover:text-red-600">
            info@pawsonrecord.com
          </a>
          . Operating entity: <strong>Refuel LLC</strong> dba Paws on Record.
        </p>
      </>
    ),
  },
];

export default function Legal() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="legal-page">
      <SEO
        title="Legal · Terms, Privacy, Editorial Policy & Corrections"
        description="Terms of Use, Privacy Policy, takedown and correction procedures for Paws on Record. All records sourced from public government registries and verified court convictions."
        path="/legal"
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3">
          <div className="sticky top-24 border border-zinc-900 bg-white">
            <div className="bg-zinc-900 text-white px-5 py-4 border-b border-red-600">
              <div className="label-eyebrow text-red-500">Section · Legal</div>
              <div className="font-display font-black text-xl mt-1">Contents</div>
            </div>
            <nav className="p-2">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-zinc-100 border-l-2 border-transparent hover:border-red-600"
                  data-testid={`legal-nav-${s.id}`}
                >
                  {s.title}
                </a>
              ))}
            </nav>
            <div className="p-4 border-t border-zinc-200 bg-zinc-50">
              <div className="label-eyebrow">Last updated</div>
              <div className="text-sm font-semibold mt-1">February 2026</div>
              <div className="text-[11px] font-mono text-zinc-500 mt-1">v1.6</div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="label-eyebrow">Section · Legal Coverage</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            Terms, Privacy & Takedowns
          </h1>
          <p className="text-zinc-700 mt-4 max-w-2xl leading-relaxed">
            Paws on Record is a public-interest transparency tool. These terms
            exist so the Service can stay useful, truthful, and — above all —
            safe for the animals and communities it serves.
          </p>

          <div className="mt-10 border-2 border-red-600 bg-red-50 p-5 flex items-start gap-4">
            <AlertTriangle size={20} className="text-red-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-900">
              <strong className="uppercase tracking-widest text-[11px] font-mono block mb-1">
                Not a background-check report
              </strong>
              Paws on Record is <strong>not a consumer reporting agency</strong> and
              does not produce consumer reports under the Fair Credit Reporting
              Act. Do not use the Service for decisions covered by the FCRA —
              such as employment, tenancy, insurance, or credit — without an
              authorized background-check provider.
            </div>
          </div>

          <div className="mt-10 space-y-10">
            {sections.map((s, i) => (
              <section
                key={s.id}
                id={s.id}
                className="border-t border-zinc-300 pt-8 scroll-mt-28"
                data-testid={`legal-section-${s.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-zinc-900 text-white border border-zinc-900 flex items-center justify-center">
                    <s.icon size={18} />
                  </div>
                  <div>
                    <div className="label-eyebrow">§ {i + 1}</div>
                    <h2 className="font-display font-black text-2xl tracking-tighter">
                      {s.title}
                    </h2>
                  </div>
                </div>
                <div className="text-zinc-700 leading-relaxed max-w-3xl">{s.body}</div>
              </section>
            ))}
          </div>

          <div className="mt-16 border border-zinc-900 p-6 bg-zinc-50">
            <div className="label-eyebrow mb-2">Copyright</div>
            <p className="text-sm text-zinc-700 leading-relaxed">
              © {new Date().getFullYear()} Refuel LLC dba Paws on Record. All rights reserved.
              Source records remain the property of their issuing agencies.
              Third-party trademarks, logos, and case captions are the property
              of their respective owners and are used here for identification
              and source attribution only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
